import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ChevronLeft, ChevronRight, Copy, GitCompareArrows } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { VersionEntry } from '@/services/shared/versionHistoryApi'
import { copyToClipboard } from '@/utils/clipboard'
import { formatDateTime } from '@/utils/format'
import { diffJson, jsonLines, parseJsonOrNull, type JsonFieldChange, type JsonLine } from '@/utils/jsonDiff'
import { JsonChangeRow, JsonColumnShell, JsonPane, type JsonLineTone } from './JsonView'

/** One version, prepared for its columns. */
interface Step {
  number: number
  version: VersionEntry
  json: unknown
  /** Null for the version that created the object. */
  changes: JsonFieldChange[] | null
  /** Paths the version before already had, so a new container can be told apart. */
  olderPaths: Set<string> | null
}

interface VersionColumnsProps {
  /** Newest first, as the API pages them. */
  versions: VersionEntry[]
  /** The version number of `versions[0]`. */
  newestNumber: number
}

/**
 * The versions of an object side by side, oldest on the left: each version's
 * full JSON with the lines that changed lit up, and between every two versions
 * a column listing what changed. Opens scrolled to the newest.
 */
export function VersionColumns({ versions, newestNumber }: VersionColumnsProps) {
  const { t } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)

  const steps = useMemo<Step[]>(() => {
    return versions
      .map((version, i) => {
        const json = parseJsonOrNull(version.json)
        // The oldest on a page has no neighbour here; the backend rebuilds its predecessor.
        const older = parseJsonOrNull(version.previousJson ?? versions[i + 1]?.json)
        return {
          number: newestNumber - i,
          version,
          json,
          changes: older && json ? diffJson(older, json) : null,
          olderPaths: older ? new Set(jsonLines(older).map((line) => line.path)) : null,
        }
      })
      .reverse()
  }, [versions, newestNumber])

  useEffect(() => {
    const el = scrollerRef.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [steps])

  const scrollByColumn = (direction: 1 | -1) =>
    scrollerRef.current?.scrollBy({ left: direction * 448, behavior: 'smooth' })

  /** Brings a changed line into view in the version it landed in. */
  const revealChange = (number: number, path: string) => {
    const line = scrollerRef.current?.querySelector<HTMLElement>(
      `[data-json-pane="${number}"] [data-path="${CSS.escape(path)}"]`
    )
    if (!line) return
    line.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    line.animate(
      [{ outline: '2px solid var(--ad-primary)' }, { outline: '2px solid transparent' }],
      { duration: 1200 }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon-sm" onClick={() => scrollByColumn(-1)} aria-label={t('versionHistory.older')}>
          <ChevronLeft />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={() => scrollByColumn(1)} aria-label={t('versionHistory.newer')}>
          <ChevronRight />
        </Button>
      </div>
      <div ref={scrollerRef} className="flex snap-x snap-proximity gap-3 overflow-x-auto pb-3">
        {steps.map((step) => (
          <div key={step.number} className="contents">
            {step.changes && (
              <ChangesColumn
                from={step.number - 1}
                to={step.number}
                changes={step.changes}
                onReveal={(path) => revealChange(step.number, path)}
              />
            )}
            <JsonColumn step={step} />
          </div>
        ))}
      </div>
    </div>
  )
}

function ChangesColumn({
  from,
  to,
  changes,
  onReveal,
}: {
  from: number
  to: number
  changes: JsonFieldChange[]
  onReveal: (path: string) => void
}) {
  const { t } = useTranslation()
  return (
    <JsonColumnShell
      className="w-xs shrink-0 snap-start border-dashed"
      header={
        <div className="flex items-center gap-2 text-sm font-medium">
          <GitCompareArrows className="size-4 text-(--ad-text-muted)" />
          <span className="font-mono">v{from}</span>
          <ArrowRight className="size-3.5 text-(--ad-text-muted)" />
          <span className="font-mono">v{to}</span>
          <Badge variant="gray" className="ml-auto">
            {t('versionHistory.fieldsChanged', { count: changes.length })}
          </Badge>
        </div>
      }
    >
      {changes.length === 0 && (
        <p className="p-3 text-xs text-(--ad-text-muted)">{t('versionHistory.noFieldChanges')}</p>
      )}
      {changes.map((change) => (
        <JsonChangeRow
          key={change.path}
          change={change}
          disabled={change.after === undefined}
          onClick={() => onReveal(change.path)}
        />
      ))}
    </JsonColumnShell>
  )
}

function JsonColumn({ step }: { step: Step }) {
  const { t } = useTranslation()
  const changed = useMemo(
    () => new Map((step.changes ?? []).map((change) => [change.path, change])),
    [step.changes]
  )
  const { version } = step

  const lineTone = (line: JsonLine): JsonLineTone => {
    if (!step.olderPaths) return null
    if (line.open || line.close) return line.path && !step.olderPaths.has(line.path) ? 'added' : null
    const change = changed.get(line.path)
    if (!change) return null
    return change.before === undefined ? 'added' : 'changed'
  }

  return (
    <JsonColumnShell
      className="w-md shrink-0 snap-start"
      header={
        <>
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className="font-mono tabular-nums">
              v{step.number}
            </Badge>
            <span className="truncate font-medium">
              {version.user?.name || t('versionHistory.unknownUser')}
            </span>
            {!step.changes && <Badge variant="success">{t('versionHistory.created')}</Badge>}
            <Button
              variant="ghost"
              size="icon-xs"
              className="ml-auto"
              onClick={() => copyToClipboard(JSON.stringify(step.json, null, 2))}
              aria-label={t('common.copyToClipboard')}
            >
              <Copy />
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs text-(--ad-text-muted)">
            <span title={version.timestamp} className="tabular-nums">
              {formatDateTime(version.timestamp, { dateStyle: 'medium', timeStyle: 'medium' })}
            </span>
            {version.masqueraderUser && (
              <span className="truncate">
                {t('audit.masqueradedBy', { name: version.masqueraderUser.name })}
              </span>
            )}
          </div>
        </>
      }
    >
      <JsonPane value={step.json} paneId={String(step.number)} tone={lineTone} />
    </JsonColumnShell>
  )
}
